import RecognitionHubViews from "@irati/recognition/constants/hubViewTypes"
import {
  CompanyValue,
  FeedEntityType,
  GroupCelebration,
  Recognition,
  RecognitionVisibility,
  SchedulerEvent,
  WalletEntityType
} from "@irati/types"
import {get} from "lodash"
import walletTracking from "@irati-tracking/wallet/rewardPointsWallet"
import walletService from "@irati/wallet/graph/wallet"
import authentication from "@irati/auth"
import Currency from "@irati/payments/enums/Currency"
import loadCompanyValues from "@irati/companyvalues/apollo/query/LoadCompanyValues"
import service, {TrackRecognitionEventData} from "@irati-tracking/tracker"
import {RECOGNITION} from "@irati-tracking/tracker/products"
import {
  RecognitionConfirmationProps,
  CreateNewRecognitionProps,
  RecognitionCTAClickProps,
  RewardCreatedOnRecognitionProps,
  RewardOnRecognitionErrorProps,
  RewardCreatedOnGreetingCardProps,
  RewardOnGreetingCardErrorProps
} from "./types"

interface ViewRecognitionDetailsProps {
  recognition: Recognition
  source: "recognition_feed" | "deeplink"
}
export type LaunchRecognitionProps = {
  recognition: Recognition | SchedulerEvent
  rewardPoints: number
  isScheduled: boolean
  source?: "celebration_onboarding"
  msgTemplateType?: string
  msgTemplateKind?: "edited" | "default"
}
class RecognitionTracking {
  defaultStatusCode = "200"

  getCreateRecognitionExtraData() {
    const rpWalletPromise = walletService
      .get(authentication.user().uuid, Currency.RP, WalletEntityType.Reward)
      .then((rpWallet) => rpWallet.availableBalance || 0)
    const companyValuesPromise = loadCompanyValues({
      limit: 9999,
      tenantId: authentication.userTenant().uuid
    }).then(({results}: {results: CompanyValue[]}) => results.length)
    return Promise.all([rpWalletPromise, companyValuesPromise])
  }

  startCreateRecognition = async (
    source: "button" | "textbox" | "rp_widget_manager" = "button",
    type: "celebration" | "greeting_card" = "celebration"
  ) => {
    const [rpPoints, companyValuesNumber] = await this.getCreateRecognitionExtraData()
    service.trackRecognitionEvent("start_create_recognition", {
      source,
      rp_wallet: rpPoints,
      values: companyValuesNumber,
      type
    })
  }

  createNewRecognition = async ({type}: CreateNewRecognitionProps) => {
    const [rpPoints, companyValuesNumber] = await this.getCreateRecognitionExtraData()
    service.trackRecognitionEvent("start_create_recognition", {
      source: "confirmation_modal",
      type,
      rp_wallet: rpPoints,
      values: companyValuesNumber
    })
  }

  launchRecognition = (props: LaunchRecognitionProps) => {
    const {recognition, rewardPoints, isScheduled = false, source, msgTemplateType, msgTemplateKind} = props
    let data: TrackRecognitionEventData = {
      statusCode: this.defaultStatusCode,
      has_reward: !!rewardPoints,
      global_reward: rewardPoints || 0,
      type: isScheduled ? "scheduled" : "standard",
      kind: "standard_recognition",
      source,
      msg_template_kind: msgTemplateKind,
      msg_template_type: msgTemplateType
    }
    if (isScheduled) {
      const scheduledRecognition = recognition as SchedulerEvent
      data = {
        ...data,
        occasion: !!scheduledRecognition.payload?.publishRecognition?.occasionId,
        recipients: scheduledRecognition.payload?.publishRecognition?.receiverIds?.length!,
        values: scheduledRecognition.payload?.publishRecognition?.companyValues?.length || 0
      }
    } else {
      const createdRecognition = recognition as Recognition
      data = {
        ...data,
        occasion: !!createdRecognition.occasion,
        recipients: createdRecognition.receivers?.results.length!,
        values: createdRecognition.companyValues?.results.length || 0
      }
    }
    service.trackRecognitionEvent("launch_recognition", data)
  }

  launchGroupCelebration = (
    recognition: GroupCelebration,
    rewardPoints: number,
    kind: "greeting_card_create" | "greeting_card_add_message" = "greeting_card_create"
  ) => {
    const data: TrackRecognitionEventData = {
      statusCode: this.defaultStatusCode,
      has_reward: !!rewardPoints,
      greeting_type: recognition.greeting.name!,
      kind,
      global_reward: rewardPoints || 0,
      type: "scheduled"
    }
    service.trackRecognitionEvent("launch_recognition", data)
  }

  errorsRecognition = (response) => {
    const statusCode = get(response, "graphQLErrors[0].apiError.response.status", null)
    const data = {statusCode: `${statusCode}`}
    service.trackRecognitionEvent("recognition_error", data)
  }

  cancelRecognition = () => {
    service.trackRecognitionEvent("cancel_recognition")
  }

  recognitionCtaClick = (data: RecognitionCTAClickProps) => {
    service.trackRecognitionEvent("recognition_cta_click", data)
  }

  rewardCreatedOnRecognition = ({type}: RewardCreatedOnRecognitionProps) => {
    service.trackRecognitionEvent("reward_wallet_reward_employees_success", {
      source: "manager_reward",
      type
    })
  }

  rewardCreatedOnGreetingCard = (data: RewardCreatedOnGreetingCardProps) => {
    service.trackRecognitionEvent("reward_wallet_reward_employees_success", data)
  }

  rewardErrorOnRecognition = (data: RewardOnRecognitionErrorProps) => {
    service.trackRecognitionEvent("reward_wallet_reward_employees_error", data)
  }

  rewardErrorOnGreetingCard = (data: RewardOnGreetingCardErrorProps) => {
    service.trackRecognitionEvent("reward_wallet_reward_employees_error", data)
  }

  clickTopupCTA = () => {
    service.trackCTAEvent({
      source: "manager_recognition",
      type: "recognition_form_topup",
      perkbox_product: RECOGNITION
    })
  }

  clickBuyPoints = () => {
    walletTracking.buyPoints({
      perkbox_product: RECOGNITION,
      role: authentication.isGlobalAdmin() ? "Admin" : "Manager",
      source: "manager_recognition",
      type: "recognition_form_topup"
    })
  }

  clickRewardBtn = (perkbox_product: string, viewType?: RecognitionHubViews) => {
    if (!viewType) {
      return
    }
    service.trackCTAEvent({
      source: "add_reward_button",
      type: viewType === RecognitionHubViews.POPUP_VIEW ? "recognition_detail" : "feed_card",
      perkbox_product
    })
  }

  recognitionConfirmation = ({type, source}: RecognitionConfirmationProps) => {
    service.trackRecognitionEvent("recognition_confirmation", {
      source,
      type
    })
  }

  viewRecognitionDetails = (props: ViewRecognitionDetailsProps) => {
    const {recognition, source = "recognition_feed"} = props
    const privacy = recognition.private
      ? RecognitionVisibility.Private
      : recognition.teams?.results.length
      ? RecognitionVisibility.Teams
      : RecognitionVisibility.Everyone
    service.trackRecognitionEvent("recognition_view_detail", {
      source,
      type: FeedEntityType.Recognition,
      reward_count: recognition.walletTransferIds?.length,
      comment_count: recognition.comments?.results.length,
      recipient_count: recognition.receivers?.results.length,
      privacy: privacy.toLowerCase()
    })
  }

  trackInviteCollaborators() {
    service.trackRecognitionEvent("recognition_invite", {
      source: "greeting_card",
      type: "invite_collaborators"
    })
  }

  viewGreetingCardDetails = (source: "recognition_feed" | "my_recognition") => {
    service.trackRecognitionEvent("recognition_view_detail", {
      source,
      type: "greeting_card"
    })
  }

  dismissOnboarding() {
    service.trackCTAEvent({
      source: "creation_nudge",
      type: "onboarding_flow_dismissed",
      perkbox_product: RECOGNITION
    })
  }

  completeOnboarding() {
    service.trackCTAEvent({
      source: "creation_nudge",
      type: "onboarding_flow_completed",
      perkbox_product: RECOGNITION
    })
  }

  viewOnboarding() {
    service.trackOnboardingEvent("launch_onboarding", {
      source: "creation_nudge",
      type: "onboarding_flow_view",
      perkbox_product: RECOGNITION
    })
  }
}
export default new RecognitionTracking()
